export type ActivePaymentMethod = "CardPayment" | "QiCard" | "ZainCash" | "Wallet";

export interface ActivePaymentSession {
  orderId: string;
  type: ActivePaymentMethod;
  transactionId?: string;
  completed?: boolean;
  /**
   * Set when the customer pressed Pay in the HyperPay card form. The browser then leaves the
   * page for HyperPay / 3-D Secure, which fires `pagehide` — that is NOT the customer abandoning
   * the payment, so the tab-close guard must not cancel it.
   */
  gatewaySubmittedAt?: number;
}

const ABANDONED_ORDERS_KEY = "payment.abandonedOrderIds";
const ACTIVE_SESSION_KEY = "payment.activeSession";
const ORDER_FOR_TXN_PREFIX = "payment.orderForTxn.";

function readAbandonedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(ABANDONED_ORDERS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((id) => String(id)).filter(Boolean));
  } catch {
    return new Set();
  }
}

function writeAbandonedSet(ids: Set<string>): void {
  localStorage.setItem(ABANDONED_ORDERS_KEY, JSON.stringify([...ids]));
}

export function markOrderPaymentAbandoned(orderId: string | number | null | undefined): void {
  const id = String(orderId ?? "").trim();
  if (!id) return;
  const ids = readAbandonedSet();
  ids.add(id);
  writeAbandonedSet(ids);
}

export function clearOrderPaymentAbandoned(orderId: string | number | null | undefined): void {
  const id = String(orderId ?? "").trim();
  if (!id) return;
  const ids = readAbandonedSet();
  if (!ids.delete(id)) return;
  writeAbandonedSet(ids);
}

export function isOrderPaymentAbandoned(orderId: string | number | null | undefined): boolean {
  const id = String(orderId ?? "").trim();
  if (!id) return false;
  return readAbandonedSet().has(id);
}

export function linkTransactionToOrder(
  transactionId: string,
  orderId: string | number
): void {
  const txId = String(transactionId ?? "").trim();
  const ordId = String(orderId ?? "").trim();
  if (!txId || !ordId) return;
  localStorage.setItem(`${ORDER_FOR_TXN_PREFIX}${txId}`, ordId);
}

export function getOrderIdForTransaction(transactionId: string | null | undefined): string | null {
  const txId = String(transactionId ?? "").trim();
  if (!txId) return null;
  return localStorage.getItem(`${ORDER_FOR_TXN_PREFIX}${txId}`)?.trim() || null;
}

export function setActivePaymentSession(session: ActivePaymentSession): void {
  if (!session.orderId?.trim()) return;
  sessionStorage.setItem(
    ACTIVE_SESSION_KEY,
    JSON.stringify({ ...session, completed: false })
  );
}

export function markActivePaymentCompleted(): void {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ActivePaymentSession;
    sessionStorage.setItem(
      ACTIVE_SESSION_KEY,
      JSON.stringify({ ...parsed, completed: true })
    );
  } catch {
    /* ignore */
  }
}

/** Card form submitted to HyperPay — the next `pagehide` is the gateway redirect, not a close. */
export function markActivePaymentGatewaySubmitted(): void {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ActivePaymentSession;
    sessionStorage.setItem(
      ACTIVE_SESSION_KEY,
      JSON.stringify({ ...parsed, gatewaySubmittedAt: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

export function clearActivePaymentSession(): void {
  sessionStorage.removeItem(ACTIVE_SESSION_KEY);
}

export function loadActivePaymentSession(): ActivePaymentSession | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActivePaymentSession;
    if (!parsed?.orderId?.trim()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearAbandonOnPaymentSuccess(
  orderId?: string | number | null,
  transactionId?: string | null
): void {
  const linkedOrderId = transactionId ? getOrderIdForTransaction(transactionId) : null;
  const resolvedOrderId = String(orderId ?? linkedOrderId ?? "").trim();
  if (resolvedOrderId) {
    clearOrderPaymentAbandoned(resolvedOrderId);
  }
  markActivePaymentCompleted();
  clearActivePaymentSession();
}
